import { Location } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { applyTransaction, OrArray } from '@datorama/akita';
import { APP_UX, IS_PROD_BUILD } from '@y42/environment';
import { assertNonNullable } from '@y42/util/assert';
import { ensureArray } from '@y42/util/ensure-array';
import {
  buffer,
  catchError,
  combineLatest,
  debounceTime,
  filter,
  firstValueFrom,
  map,
  of,
  shareReplay,
  Subject,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import {
  GitEventBranchChanged,
  GitEventBranchCreated,
  GitEventBranchMerged,
  GitEventBranchPulled,
  GitEventChangesCommitted,
  GitEventChangesDiscarded,
  GitEventChangesRestored,
  GitEventChangesReverted,
  GitEventChangesStashed,
  GitEventForceUpdateUI,
  GitEventType,
} from '../state/git-event.model';
import { GitQuery } from '../state/git.query';
import { GitStore } from '../state/git.store';
import { contentHasConflicts } from '../state/util/resolve-conflict';
import { safeJsonParse } from '../state/util/safe-json-parse';
import { safeYAMLParse } from '../state/util/safe-yaml-parse';
import { FSAction } from '../worker/actions/actions.enum';
import type { FSDeleteFileAction } from '../worker/actions/fs-delete-file';
import type { FSMkDirAction } from '../worker/actions/fs-mk-dir';
import type { FSReadDirAction } from '../worker/actions/fs-read-dir';
import type { FSReadFileAction } from '../worker/actions/fs-read-file';
import type { FSReadFilesAction } from '../worker/actions/fs-read-files';
import type { FSRenameEntryAction } from '../worker/actions/fs-rename-entry';
import type { FSRmDirAction } from '../worker/actions/fs-rm-dir';
import type { FSWriteFileAction } from '../worker/actions/fs-write-file';
import type { FSWriteJSONAction } from '../worker/actions/fs-write-json';
import type { GitStashAction } from '../worker/actions/git-stash';
import { extractStashEntries } from '../worker/actions/git-stash-extract-entries';
import { GitStatusToStatusRow } from '../worker/actions/git-status-short';
import { GitPullWithConflictError } from '../worker/errors/git-pull-with-conflict.git-error';
import type {
  FilePath,
  GitAheadBehindStatus,
  GitBlameLine,
  GitBranch,
  GitBranchFrom,
  GitCommit,
  GitCompareDirection,
  GitConflict,
  GitDiff,
  GitFileChange,
  GitFileTreeWithContents,
  GitRef,
  GitRepo,
  GitStashEntry,
  GitTreeItem,
  StatusRow,
} from '../worker/models/git-worker.model';
import type { GitError } from '../worker/wasm-git-worker-errors.model';
import { WasmGitService } from '../worker/wasm-git.service';
import {
  GitFSErrno,
  GitFSErrnoError,
  WorkerAction,
  WorkerActionParams,
  WorkerFnParams,
} from '../worker/wasm-git.worker.model';

interface Y42Window {
  git: (commands: string) => void;
  fs: {
    readFile: (params?: WorkerActionParams<FSReadFileAction>) => void;
    writeFile: (params?: WorkerActionParams<FSWriteFileAction>) => void;
    deleteFile: (params?: WorkerActionParams<FSReadFileAction>) => void;
    readDir: (params?: WorkerActionParams<FSReadDirAction>) => void;
    mkDir: (params?: WorkerActionParams<FSMkDirAction>) => void;
    rmDir: (params?: WorkerActionParams<FSRmDirAction>) => void;
  };
}

type FSCallableAction =
  | FSWriteFileAction
  | FSReadFileAction
  | FSReadFilesAction
  | FSMkDirAction
  | FSReadDirAction
  | FSDeleteFileAction
  | FSRmDirAction;

const gitCall$$ = new Subject<string>();
const fsCall$$ = new Subject<{ type: FSCallableAction['type']; params: FSCallableAction['params'] }>();

try {
  (window as unknown as { y42: Y42Window }).y42 = {
    ...((window as unknown as { y42: Y42Window }).y42 || {}),
    git: (command) => gitCall$$.next(command),
    fs: {
      writeFile: (params) => fsCall$$.next({ type: FSAction.WriteFile, params }),
      readFile: (params) => fsCall$$.next({ type: FSAction.ReadFile, params }),
      deleteFile: (params) => fsCall$$.next({ type: FSAction.DeleteFile, params }),
      readDir: (params) => fsCall$$.next({ type: FSAction.ReadDir, params }),
      mkDir: (params) => fsCall$$.next({ type: FSAction.MkDir, params }),
      rmDir: (params) => fsCall$$.next({ type: FSAction.RmDir, params }),
    },
  };
} catch (err) {
  // for some reason it throws window is undefined error
}

export const WRITE_BUFFER_DEBOUNCE_TIME_MS = 5;

@Injectable({ providedIn: 'root' })
export class GitService {
  constructor(
    private readonly gitStore: GitStore,
    private readonly gitQuery: GitQuery,
    private readonly wasmGit: WasmGitService,
    private readonly router: Router,
    private readonly location: Location,
  ) {
    gitCall$$
      .pipe(
        withLatestFrom(this.wasmGit.fsInitialized$),
        filter(([_, initialized]) => initialized),
        map(([git]) => git),
        switchMap((command) => this.gitCall(command, {}).catch(() => null)),
        // catch(() => null) is used inside switchMap in order not to stop outer obs to emit values on error
        tap((response) => (response ? console.debug(response) : null)),
        switchMap(() => this.updateUIState({ branches: true, files: true, aheadBehind: true, commitSha: true })),
        catchError((err: unknown) => {
          console.error(err);
          return of(null);
        }),
      )
      .subscribe();

    fsCall$$
      .pipe(
        withLatestFrom(this.wasmGit.fsInitialized$),
        filter(([_, initialized]) => initialized),
        map(([fs]) => fs),
        // catch(() => null) is used inside switchMap in order not to stop outer obs to emit values on error
        switchMap(({ type, params }) => this.wasmGit.call({ type, params }).catch(() => null)),
        tap((response) => console.debug(response)),
        switchMap(() => this.updateUIState({ branches: true, files: true, aheadBehind: true, commitSha: true })),
        catchError(() => of(null)),
      )
      .subscribe();
  }

  readonly isUX3 = inject(APP_UX) === 'ux3';

  private authTokenFn!: NonNullable<GitRepo['authTokenFn']>;
  private readonly production = IS_PROD_BUILD;

  private readonly writeFile$ = new Subject<[File, boolean]>();
  private readonly writeFilesBuffer$ = this.writeFile$.pipe(
    buffer(this.writeFile$.pipe(debounceTime(WRITE_BUFFER_DEBOUNCE_TIME_MS))),
    switchMap((emits) =>
      this.writeFiles(
        emits.map((emit) => emit[0]),
        emits.some((emit) => emit[1]),
      ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  protected readonly filesChanged$$ = new Subject<FilePath[]>();
  readonly filesChanged$ = this.filesChanged$$.asObservable();

  readonly ready$ = combineLatest([
    this.gitQuery.gitStatusCode$.pipe(filter((status) => status === null)),
    this.gitQuery.nameOfInitializedRepo$.pipe(filter((name) => name !== null)),
  ]).pipe(shareReplay({ bufferSize: 1, refCount: true }));

  readonly events$ = this.gitStore.event$.pipe(shareReplay({ bufferSize: 1, refCount: true }));

  async load(repo: GitRepo): Promise<boolean> {
    const { repoDir, spaceId, spaceName, url, userEmail, userName, authTokenFn, branch } = repo;

    this.authTokenFn = authTokenFn;

    if (await firstValueFrom(this.gitQuery.loading$)) {
      return await firstValueFrom(
        this.gitQuery.nameOfInitializedRepo$.pipe(
          filter((name) => name === repoDir),
          map(() => true),
        ),
      );
    }

    if ((await firstValueFrom(this.gitQuery.nameOfInitializedRepo$)) === repoDir) {
      return true;
    }

    console.debug(`[Git]: loading repo "${repoDir}"`);

    applyTransaction(() => {
      this.gitStore.setLoading(true);
      this.gitStore.setStatus('init');
      this.gitStore.setRepoInitialized(null);
    });

    try {
      await this.wasmGit.initFS({ repoDir, userName, userEmail });
      const { tree } = await this.wasmGit.readDir({
        excludeChildren: true,
      });

      const isCloned = Object.keys(tree).length > 0;
      console.debug(`[Git]: repo "${repoDir}" ${isCloned ? 'already' : 'not yet'} cloned`);
      if (!isCloned) {
        this.gitStore.setStatus('clone');

        await this.wasmGit.gitClone({
          repoDir,
          spaceId,
          spaceName,
          url,
          userEmail,
          userName,
          productionMode: this.production,
          authToken: await this.authTokenFn(),
        });
      }
    } catch (err) {
      this.gitStore.setStatus({
        status: 'clone',
        error: err as GitError,
      });

      throw err;
    }

    this.gitStore.setGitRepo(repo);

    try {
      this.gitStore.setStatus('checkout');

      await this.updateListOfBranches();

      // need to set active branch before "git fetch"
      // because if we navigate to the app with a URL
      // that contains a different branch to be checked out
      // then "git fetch" will fail
      await this.setActiveBranch({
        branch,
        ignoreRedirection: true,
      });

      if (this.isUX3) {
        await Promise.all([this.updateFileStatuses(), this.checkNumberOfConflictedFiles()]);
      } else {
        // Run these concurrently for faster startup
        await Promise.all([this.fetchChanges(), this.updateFileStatuses(), this.checkNumberOfConflictedFiles()]);

        try {
          await this.pullChanges();
        } catch (err) {
          if (err instanceof Error && err.message.includes('conflict')) {
            // do not care about conflict
            // setting status == conflict
            // and resolving later
          } else {
            throw err;
          }
        }
      }

      this.gitStore.setRepoInitialized(repo.repoDir);
    } catch (err) {
      console.error(err);
      this.gitStore.setStatus({
        status: 'fetch',
        error: err as GitError,
      });
      throw err;
    } finally {
      this.gitStore.setLoading(false);
    }

    return true;
  }

  /**
   * Calls `git checkout` on the given branch.
   *
   * @param branch If no branch is passed it will just update the store with the currently checked out branch.
   * @param ignoreRedirection Set to true to avoid navigation to the selected branch
   */
  async setActiveBranch({
    branch,
    ignoreRedirection,
  }: {
    branch?: GitBranch['name'];
    ignoreRedirection?: boolean;
  } = {}) {
    this.gitStore.setLoading(true);
    this.gitStore.setStatus({ status: 'checkout' });
    const activeBranch = await this.getCurrentBranch();

    if (!branch) {
      branch = activeBranch;
    }

    if (branch !== activeBranch) {
      const branches = this.gitQuery.getAll();

      branch = branches.find(({ name }) => name === branch)?.name || branches[0]?.name;

      console.debug(`[Git]: setting active branch "${branch}"`);
      try {
        this.gitStore.setStatus({ status: 'checkout', params: branch });
        await this.wasmGit.gitCheckout({ branch });
      } catch (err) {
        console.error(`[Git]: could not checkout branch "${branch}"`, err);
        await this.setActiveBranch({
          branch: activeBranch,
        });
        if (/unresolved conflict|conflicts? prevent/.test(err instanceof Error ? err.message : `${err}`)) {
          this.gitStore.setLoading(false);
          this.gitStore.setStatus({ status: 'checkout-conflict', params: branch });
        }
        throw err;
      }
    }

    await applyTransaction(async () => {
      this.gitStore.setActive(branch!);
      await this.updateUIState({
        files: true,
        aheadBehind: true,
        commitSha: true,
      });
      this.gitStore.setLoading(false);
      this.gitStore.setStatus(null);

      if (!ignoreRedirection) {
        // TODO: Gabi - expose observable and create side effect service for navigation, then remove spaceName
        const repoDir = this.gitQuery.getRepo()?.repoDir;
        assertNonNullable(repoDir);

        const orgSlug = repoDir.split('/')[0];
        const spaceName = this.gitQuery.getRepo()?.spaceName;
        assertNonNullable(spaceName);

        const urlPaths = this.location.path().split('/');

        const GIT_BRANCH_ROUTE_URL_PARAM = this.isUX3 ? 'branch' : 'git';

        const url = `/org/${orgSlug}/space/${spaceName}/${GIT_BRANCH_ROUTE_URL_PARAM}/${branch}${
          urlPaths.length > 7 ? `/${urlPaths.slice(7).join('/')}` : ''
        }`;
        console.debug(`[Git]: redirecting to "${url}"`);
        this.router.navigateByUrl(url);
      }
    });
    this.gitStore.emit<GitEventBranchChanged>(GitEventType.BranchChanged, {
      to: branch || '',
    });
  }

  setActiveTreeItem(item: GitTreeItem) {
    this.gitStore.updateUI({ activeTreeItem: item });
  }

  async applyStashedChanges() {
    this.stash(['apply', '0', '--force']);
  }

  async stash(params?: WorkerActionParams<GitStashAction>) {
    console.debug(`[Git]: stashing changes`);
    try {
      this.gitStore.setStatus('stash');
      if (!params) {
        // stage files when saving stash
        await this.wasmGit.gitAdd();
      }
      await this.wasmGit.gitStash(params);

      await applyTransaction(async () => {
        await this.updateFileTree();
        await this.updateFileStatuses();
        await this.checkNumberOfConflictedFiles();
        this.gitStore.setStatus(null);
      });
      this.gitStore.emit<GitEventChangesStashed>(GitEventType.ChangesStashed, {
        branch: this.gitQuery.getActiveBranchName()!,
      });
    } catch (err) {
      console.error(`[Git]: error stashing changes`, err);
    }
  }

  /** Clears all branch stashes [UX2] */
  async clearBranchStashes() {
    const stashedChanges = await this.getBranchStashes();
    await stashedChanges.map((stashEntry) => this.stash(['drop', stashEntry.index]));
  }

  /** Clears first stash [UX3] */
  async clearFirstStash() {
    const stashedChanges = await this.getStashes();
    const firstStash = stashedChanges[0];

    if (firstStash) {
      await this.stash(['drop', firstStash.index]);
    }
  }

  async restoreStash(index: string) {
    await this.stash(['apply', index, '--force']);
    await this.stash(['drop', index]);
    this.gitStore.emit<GitEventChangesRestored>(GitEventType.ChangesRestored, {
      branch: this.gitQuery.getActiveBranchName()!,
    });
  }

  /** Returns stashes for all branches [UX3] */
  async getStashes() {
    let stashLog = '';
    try {
      stashLog = (await this.wasmGit.readFile({ path: '.git/logs/refs/stash' })).trim();
    } catch (e) {
      // doesn't exist
    }

    return extractStashEntries(await this.wasmGit.gitStash(['list']), stashLog);
  }

  /** Returns stashes for the active branch [UX2] */
  async getBranchStashes(): Promise<GitStashEntry[]> {
    const branch = await this.getCurrentBranch();
    const stashes = await this.getStashes();

    return stashes.filter((entry) => entry.branchName === branch);
  }

  /** Returns first stashed changes for all branches [UX3] */
  async getStashedChanges(): Promise<GitDiff[]> {
    const stashes = await this.getStashes();
    const firstStashSha = stashes[0]?.sha;

    if (!firstStashSha) {
      return [];
    }

    return this.getDiff(`${firstStashSha}^`, firstStashSha);
  }

  /** Returns first stashed changes for the active branch [UX2] */
  async getBranchStashedChanges(): Promise<GitDiff[]> {
    const stashes = await this.getBranchStashes();
    const firstStashSha = stashes[0]?.sha;

    if (!firstStashSha) {
      return [];
    }

    return this.getDiff(`${firstStashSha}^`, firstStashSha);
  }

  async createBranch(branchName: string, branchFrom: GitBranchFrom = { use: 'active' }, commitSha?: string) {
    console.debug(`[Git]: creating branch "${branchName}" from ${branchFrom}`);
    let branchFromName: string;

    if (typeof branchFrom === 'object' && branchFrom.use === 'default') {
      const defaultBranchName = this.gitQuery.getDefaultBranchName();
      branchFromName = defaultBranchName;
      await this.setActiveBranch({
        branch: defaultBranchName,
      });
    } else if (typeof branchFrom === 'string') {
      branchFromName = branchFrom;
      await this.setActiveBranch({
        branch: branchFrom,
      });
    } else {
      // active branch
      branchFromName = this.gitQuery.getActiveBranchName()!;
    }

    try {
      branchName = branchName.trim();
      const activeBranch = this.gitQuery.getActiveBranch();

      await this.wasmGit.gitCheckout({ branch: branchName, create: true, commitSha });
      this.gitStore.upsert(branchName, { tree: {}, published: false, committerDate: activeBranch.committerDate });
      await this.setActiveBranch({
        branch: branchName,
      });
    } catch (err) {
      console.error(`[Git]: error creating branch "${branchName}" from ${branchFrom}`, err);
      throw err;
    }

    try {
      await this.publishBranch();
      this.gitStore.emit<GitEventBranchCreated>(GitEventType.BranchCreated, {
        from: branchFromName,
        name: branchName,
      });
    } catch (err) {
      console.error(`[Git]: error publishing branch "${branchName}"`, err);
      throw err;
    }
  }

  async createFolder(
    path: string,
    options: { skipSync: boolean; emitFilesChangedEvent?: boolean } = { skipSync: false, emitFilesChangedEvent: true },
  ) {
    const { skipSync, emitFilesChangedEvent = true } = options;
    console.debug(`[Git]: creating folder "${path}"`);
    await this.wasmGit.mkDir({ path, skipSync });
    if (emitFilesChangedEvent) {
      this.filesChanged$$.next([path]);
    }
  }

  async deleteFiles(paths: string[]): Promise<void> {
    if (!paths.length) {
      return;
    }

    this.gitStore.setStatus('delete');

    await Promise.all(
      paths.map(async (path) => {
        console.debug(`[Git]: deleting file "${path}"`);

        await this.wasmGit.deleteFile({ path, skipSync: true });
      }),
    );

    await this.wasmGit.syncFS();

    await applyTransaction(async () => {
      await Promise.all([this.updateFileTree(), this.updateFileStatuses()]);

      this.gitStore.setStatus(null);
      console.debug(`[Git]: deleted ${paths.length} files`);
    });
    this.filesChanged$$.next(paths);
  }

  async bulkDelete(paths: Array<{ path: string; type: 'dir' | 'file' }>, emitFilesChangedEvent = true): Promise<void> {
    if (!paths.length) {
      return;
    }

    this.gitStore.setStatus('delete');

    await Promise.all(
      paths.map(({ path, type }) => {
        console.debug(`[Git]: deleting "${path}"`);

        if (type === 'dir') {
          return this.wasmGit.rmDir({ path, skipSync: true });
        }
        return this.wasmGit.deleteFile({ path, skipSync: true });
      }),
    );

    await this.wasmGit.syncFS();

    await applyTransaction(async () => {
      await this.updateUIState({ files: true });

      this.gitStore.setStatus(null);
      console.debug(`[Git]: deleted ${paths.length} files/directories`);
    });

    if (emitFilesChangedEvent) {
      this.filesChanged$$.next(paths.map((item) => item.path));
    }
  }

  async getFileChanges(): Promise<GitFileChange[]> {
    if (this.gitQuery.getStatus() !== 'reset') {
      this.gitStore.setStatus('read');
    }

    const fileStatuses = await this.wasmGit.gitStatusShort();
    const branch = this.gitQuery.getActiveBranch().name;
    const modifiedFiles = this.gitQuery.getModifiedFilesList();
    const changes = await Promise.all(modifiedFiles.map((path) => this.getFileChange(branch, path)));

    this.gitStore.setFileStatuses(fileStatuses);

    if (this.gitQuery.getStatus() !== 'reset') {
      this.gitStore.setStatus(null);
    }

    return changes.map((change) => {
      const [__, ...statusRow] = fileStatuses.find(([path]) => path === change.path) || [];
      return { ...change, status: statusRow as StatusRow };
    });
  }

  async getFileConflicts(): Promise<GitConflict[]> {
    const conflictedFiles = await this.getPathsOfConflictedFiles();
    const yoursSha = 'HEAD';
    let theirsSha = '';
    try {
      theirsSha = (await this.wasmGit.readFile({ path: `.git/MERGE_HEAD` })).trim();
    } catch (e) {
      // '.git/MERGE_HEAD' might not exist
      // which means conflict comes from pull
      const activeBranch = this.gitQuery.getActiveBranch();
      theirsSha = await this.wasmGit.gitRevParse({ rev: `origin/${activeBranch.name}` });
    }

    const baseSha = await this.wasmGit.gitMergeBase({ ref1: yoursSha, ref2: theirsSha });

    const conflicts = await Promise.all(
      conflictedFiles.map(async ([path, statusRow]) => {
        let content = '';
        try {
          content = await this.wasmGit.readFile({ path });
        } catch (err) {
          // file doesn't exist
        }

        let yours = '';
        let theirs = '';
        const base = await this.wasmGit.gitShow({ branch: baseSha, path });

        if (!contentHasConflicts(content)) {
          if (
            statusRow === GitStatusToStatusRow.DU /* OURS_DELETED */ ||
            statusRow === GitStatusToStatusRow.UA /* ADDED_BY_THEM */
          ) {
            yours = '';
            theirs = content;
          }
          if (
            statusRow === GitStatusToStatusRow.UD /* THEIRS_DELETED */ ||
            statusRow === GitStatusToStatusRow.AU /* ADDED_BY_US */
          ) {
            yours = content;
            theirs = '';
          }
          if (statusRow === GitStatusToStatusRow.DD /* BOTH_DELETED */) {
            yours = '';
            theirs = '';
          }
        } else {
          yours = await this.wasmGit.gitShow({ branch: yoursSha, path });
          theirs = await this.wasmGit.gitShow({ branch: theirsSha, path });
        }

        return {
          path,
          status: statusRow,
          content,
          base,
          yours,
          theirs,
        };
      }),
    );
    this.gitStore.updateUI({ numberOfConflictedFiles: conflictedFiles?.length });

    return conflicts;
  }

  async stat(path: string) {
    return this.wasmGit.statFS({ path });
  }

  async commit(message: string, paths?: FilePath[], force?: boolean) {
    this.gitStore.setStatus('commit');
    const activeBranch = this.gitQuery.getActiveBranch();
    let modifiedFiles: string[] = [];

    if (!paths) {
      modifiedFiles = this.gitQuery.getModifiedFilesList();
      await this.wasmGit.gitAdd();
    } else {
      modifiedFiles = paths;
      this.wasmGit.gitAdd({ files: paths });
    }

    if (!force && modifiedFiles.length === 0) {
      throw new Error('[Git]: no files to commit');
    }

    console.debug(`[Git]: committing ${modifiedFiles.join(', ')} files on branch "${activeBranch.name}"`);
    await this.wasmGit.gitCommit({ message });
    this.gitStore.update(activeBranch.name, { committerDate: new Date() });
    this.gitStore.updateUI({ ahead: 1 });
    console.debug(`[Git]: committed ${modifiedFiles.length} files`);
    await this.updateCurrentLocalCommitSha();
    await this.pushChanges({ dontClearStatus: true });
    await this.updateFileStatuses();
    await this.checkNumberOfConflictedFiles();
    await this.updateAheadBehindStatus();

    this.gitStore.emit<GitEventChangesCommitted>(GitEventType.ChangesCommitted, { paths });
    this.gitStore.setStatus(null);
  }

  async pushChanges(options?: { dontClearStatus?: boolean; force?: boolean }) {
    const { dontClearStatus, force } = options || {};

    if (!force && !this.gitQuery.getCommitsAhead()) {
      return;
    }

    console.debug(`[Git]: pushing`);
    this.gitStore.setStatus('push');
    try {
      await this.wasmGit.gitPush({ authToken: await this.authTokenFn() });
      await this.updateAheadBehindStatus();
      this.gitStore.emit(GitEventType.PushSucceeded, {});
    } catch (err) {
      this.gitStore.setStatus(null);

      if (err instanceof Error && err.message.includes('not present locally')) {
        await this.fetchChanges();
        await this.pullChanges();
        if (this.gitQuery.getCommitsAhead()) {
          await this.wasmGit.gitPush({ authToken: await this.authTokenFn() });
        }
        this.gitStore.emit(GitEventType.PushSucceeded, {});
      } else {
        // Commit rejected by pre-receive hook
        const commitsAhead = this.gitQuery.getCommitsAhead();
        await this.revertCommits(commitsAhead);
        this.gitStore.emit(GitEventType.PushFailed, { err });
        throw err;
      }
    } finally {
      await this.updateLastPushedCommitSha();
    }

    const activeBranch = this.gitQuery.getActiveBranch();
    this.gitStore.update(activeBranch.name, { published: true });
    console.debug(`[Git]: pushed`);

    if (!dontClearStatus) {
      this.gitStore.setStatus(null);
    }
  }

  async publishBranch() {
    await this.pushChanges({ force: true });

    const activeBranch = this.gitQuery.getActiveBranch();
    // alternative
    // to git branch --set-upstream-to
    await this.wasmGit.gitBranch({ command: '-u', options: [`origin/${activeBranch.name}`] });
  }

  async updateActiveFile(value: string) {
    const activeTreeItem = this.gitQuery.getActiveTreeItem()!;
    return this.writeFile({ path: activeTreeItem.path, content: value });
  }

  async resolveConflicts(conflicts: GitConflict[], updatedContentType: 'yours' | 'theirs') {
    // no (real) need for error handling
    // since deleteFile can't fail
    // and writeFile can fail only
    // if the user runs out of disk space
    await Promise.all(
      conflicts.map(async (conflict) => {
        const updatedContent = conflict[updatedContentType];
        const { path } = conflict;
        if (!updatedContent) {
          await this.deleteFile({ path }, true);
        } else {
          const parsedContent = parsePathContent(path, updatedContent);
          await this.writeFile({ path, content: parsedContent || updatedContent, skipSync: true });
        }
        await this.wasmGit.gitAdd({ files: [path] });
      }),
    );

    await this.updateUIState({ files: true }, { emitEvent: true });
    this.gitStore.setStatus(null);
  }

  async applyConflictedChange(conflict: GitConflict, updatedContent: string) {
    const { path } = conflict;
    return this.updateFileContent(path, updatedContent);
  }

  async completeMerge(path: string) {
    await this.wasmGit.gitAdd({ files: [path] });
  }

  async updateFileContent(path: string, content: string) {
    const parsedContent = parsePathContent(path, content);
    await this.writeFile({ path, content: parsedContent || content, skipSync: true });
    await this.wasmGit.gitAdd({ files: [path] });
    await this.updateUIState();
  }

  async createMergeCommit(msg?: string) {
    if (!msg) {
      try {
        msg = await this.wasmGit.readFile({ path: '.git/MERGE_MSG' });
      } catch (e) {
        // might not exist
        const modifiedFiles = this.gitQuery.getModifiedFilesList();
        // don't create an empty commit in case of empty MERGE_MSG
        if (!modifiedFiles.length) {
          return;
        }
      }
    }

    await this.commit(msg || 'Resolve conflicts', undefined, true);
    await this.updateUIState({ files: false }, { emitEvent: true });
  }

  async writeFile<T extends FSWriteJSONAction | FSWriteFileAction>(
    params: WorkerActionParams<T>,
    opt: { skipSetStatus?: boolean; emitFilesChangedEvent?: boolean } = {
      skipSetStatus: false,
      emitFilesChangedEvent: true,
    },
  ) {
    const { path, content, skipSync } = params;
    const { skipSetStatus, emitFilesChangedEvent } = opt || {};

    console.debug(`[Git]: writing file "${path}"`);

    if (!skipSetStatus) {
      this.gitStore.setStatus('write');
    }

    console.debug(content);

    await applyTransaction(async () => {
      if (path.endsWith('.json')) {
        await this.wasmGit.writeJSON(params);
      } else if (path.endsWith('.yml') || path.endsWith('.yaml')) {
        await this.wasmGit.writeYAML(params);
      } else {
        await this.wasmGit.writeFile({ ...params, content: `${params.content || ''}` });
      }

      if (!skipSync) {
        await this.updateUIState({ files: true });
      }

      if (!skipSetStatus) {
        this.gitStore.setStatus(null);
      }

      console.debug(`[Git]: wrote file "${path}"`);
    });

    if (emitFilesChangedEvent) {
      this.filesChanged$$.next([path]);
    }
  }

  scheduleWriteFile(file: File, emitFilesChangedEvent = true) {
    // `firstValueFrom` should be called before `writeFile$` emits any value,
    // because as `writeFilesBuffer$` is based on `writeFile$` and `writeFile$` is a `Subject`,
    // subscriber will receive only new emits, made after subscription,
    // so subscription should go first and then emitting
    const result = firstValueFrom(this.writeFilesBuffer$);
    this.writeFile$.next([file, emitFilesChangedEvent]);
    return result;
  }

  async writeFiles(files: File[], emitFilesChangedEvent = true): Promise<void> {
    if (files.length === 0) {
      return;
    }

    this.gitStore.setStatus('write');

    await this.wasmGit.writeFiles({ files });

    await applyTransaction(async () => {
      await this.updateUIState({ files: true });
      this.gitStore.setStatus(null);
      console.debug(`[Git]: wrote ${files.length} files`);
    });

    if (emitFilesChangedEvent) {
      this.filesChanged$$.next(files.map((file) => file.path));
    }
  }

  async deleteFile(params: OrArray<Omit<WorkerActionParams<FSDeleteFileAction>, 'skipSync'>>, skipSync?: boolean) {
    params = ensureArray(params);

    console.debug(`[Git]: deleting "${params.map((param) => param.path)}"`);

    await Promise.all(params.map((param) => this.wasmGit.deleteFile({ ...param, skipSync })));

    if (!skipSync) {
      await applyTransaction(async () => {
        await this.updateUIState({ files: true });
      });
    }
    console.debug(`[Git]: deleted "${params.map((param) => param.path)}"`);

    this.filesChanged$$.next(params.map((param) => param.path));
  }

  async discardChanges(paths?: FilePath[]) {
    console.debug(`[Git]: discarding changes`);
    this.gitStore.setStatus('reset');

    if (!paths) {
      // Resetting is safer than checkouts in some cases (e.g. after an unfinished merge).
      await this.wasmGit.gitAdd();
      await this.wasmGit.gitReset(['--hard', 'HEAD']);
    } else {
      // the app doesn't stage files itself so we just
      // use 'git checkout -- path...'  which replaces the modified files in the working directory
      // with whatever is currently in the index
      // it also takes care of removing untracked paths (checkout_strategy = GIT_CHECKOUT_FORCE | GIT_CHECKOUT_REMOVE_UNTRACKED)
      await this.wasmGit.gitCheckout({ paths: paths || ['.'] });
    }

    // remove empty folders since provided paths might not cover empty untracked folders
    if (paths) {
      await this.wasmGit.gitReset();
    }

    await applyTransaction(async () => {
      await this.updateUIState({ files: true });
      this.gitStore.setStatus(null);
    });
    this.gitStore.emit<GitEventChangesDiscarded>(GitEventType.ChangesDiscarded, {
      branch: this.gitQuery.getActiveBranch().name,
    });
  }

  async readJSON<T>(path: string, cwd?: string): Promise<T | null> {
    return (await this.wasmGit.readJSON({ path, cwd })) as T;
  }

  async readFile(path: string, cwd?: string) {
    return await this.wasmGit.readFile({ path, cwd });
  }

  async readFiles(
    path: string,
    params?: { filePathPattern?: string | RegExp; fromSchemaVersion?: string; toSchemaVersion?: string },
  ): Promise<GitFileTreeWithContents> {
    return await this.wasmGit.readFiles({
      path,
      filePathPattern: params?.filePathPattern,
      fromSchemaVersion: params?.fromSchemaVersion,
      toSchemaVersion: params?.toSchemaVersion,
    });
  }

  async deleteDirectory(paths: OrArray<string>) {
    paths = ensureArray(paths);
    this.gitStore.setStatus('delete');
    console.debug(`[Git]: deleting folder(s) "${paths}"`);

    await Promise.all(paths.map((path) => this.wasmGit.rmDir({ path })));

    await applyTransaction(async () => {
      await this.updateFileTree();
      await this.updateFileStatuses();
      this.gitStore.setStatus(null);
      console.debug(`[Git]: folder "${paths}" deleted`);
    });

    this.filesChanged$$.next(paths);
  }

  /**
   * Returns the current branch from Git.
   */
  async getCurrentBranch(): Promise<string> {
    try {
      const branch = await this.wasmGit.gitBranchShowCurrent();
      console.debug(`[Git]: current branch "${branch}"`);
      return branch;
    } catch (err) {
      console.error(`[Git]: could not read current branch`);
      throw err;
    }
  }

  async fetchChanges(): Promise<void> {
    this.gitStore.setStatus('fetch');
    console.debug(`[Git]: fetching`);
    try {
      // in case if branch was already merged and removed git throws
      // `Error: could not remove directory...: still present: ` so we need to repeat the call
      await repeatOnFail(async () => this.wasmGit.gitFetch({ authToken: await this.authTokenFn(), prune: true }));
      await this.updateUIState({ files: true, branches: true, aheadBehind: true });
      await this.fastForwardBranches();
    } catch (err) {
      console.error(`[Git]: could not fetch changes`, err);
      this.gitStore.setError(err);
      throw err;
    } finally {
      this.gitStore.setStatus(null);
    }
  }

  async pullChanges(): Promise<void> {
    if (!this.gitQuery.getCommitsBehind()) {
      return;
    }

    console.debug(`[Git]: pulling changes`);
    const branch = this.gitQuery.getActiveBranch().name;

    try {
      this.gitStore.setStatus('pull');
      await this.wasmGit.gitPull({ branch, authToken: await this.authTokenFn() });
      await this.updateFileTree();
      await this.updateAheadBehindStatus();
      await Promise.all([this.updateCurrentLocalCommitSha(), this.updateLastPushedCommitSha()]);
    } catch (err) {
      if (this.isUX3) {
        this.gitStore.setStatus(null);

        if (err instanceof Error && (/conflicts? prevent/.test(err.message) || /conflict: /.test(err.message))) {
          throw new GitPullWithConflictError(err.message);
        }

        console.error(`[Git]: could not pull changes`, err);
      } else {
        console.error(`[Git]: could not pull changes`, err);

        if (err instanceof Error) {
          if (/conflicts? prevent/.test(err.message)) {
            await this.wasmGit.gitStash();
            await this.wasmGit.gitMerge({ branch: `origin/${branch}` });
            await this.wasmGit.gitStash(['pop']);
            await this.checkNumberOfConflictedFiles();
          }
          if (/conflict: /.test(err.message)) {
            await this.checkNumberOfConflictedFiles();
          }
        }
      }

      throw err;
    } finally {
      this.gitStore.setStatus(null);

      this.gitStore.emit<GitEventBranchPulled>(GitEventType.BranchPulled, {
        branch,
      });
    }
  }

  async renameEntry(
    params: WorkerActionParams<FSRenameEntryAction>,
    opt: {
      skipUIUpdate?: boolean;
      emitFilesChangedEvent?: boolean;
    } = { skipUIUpdate: false, emitFilesChangedEvent: true },
  ) {
    const { skipUIUpdate, emitFilesChangedEvent } = opt || {};
    await this.wasmGit.renameEntry(params);

    if (!skipUIUpdate) {
      await this.updateUIState({ files: true });
    }

    if (emitFilesChangedEvent) {
      this.filesChanged$$.next([params.oldPath, params.newPath]);
    }
  }

  async merge(branch: GitBranch) {
    try {
      this.gitStore.setStatus('merge');
      await this.wasmGit.gitMerge({ branch: normalizeBranchName(branch) });
      await this.updateCurrentLocalCommitSha();
      await this.pushChanges({ force: true });
      this.gitStore.emit<GitEventBranchMerged>(GitEventType.BranchMerged, {
        source: branch.name,
        target: this.gitQuery.getActiveBranchName()!,
      });
    } catch (err) {
      console.error(`[Git]: could not merge branch '${branch.name}'`, err);
      throw err;
    } finally {
      await this.updateUIState();
      this.gitStore.setStatus(null);
    }
  }

  async abortMerge() {
    try {
      // Ideally it should be `git merge --abort` but this functionality is missing in the git library.
      await this.wasmGit.gitReset(['--hard', 'HEAD']);
    } catch (err) {
      console.error(`[Git]: could not abort merge`, err);
      throw err;
    } finally {
      await this.updateUIState();
      this.gitStore.setStatus(null);
    }
  }

  async mergeTree(branch1: GitBranch, branch2: GitBranch) {
    return this.wasmGit.gitMergeTree({ branch1: normalizeBranchName(branch1), branch2: normalizeBranchName(branch2) });
  }

  async mergeBase(ref1: string, ref2: string) {
    return this.wasmGit.gitMergeBase({ ref1, ref2 });
  }

  async getDiff(fromSha: string, toSha: string, path?: FilePath): Promise<GitDiff[]> {
    return this.wasmGit.gitDiff({ fromSha, toSha, path });
  }

  async getBlameLines(params: { path: FilePath; commitSha?: string; contents?: string }): Promise<GitBlameLine[]> {
    const blameLines = await this.wasmGit.gitBlame(params);
    const uniqueSha = [...new Set(blameLines.map((line) => line.sha))];
    const allCommitDetails = await Promise.all(uniqueSha.map((sha) => this.getCommitDetails(sha)));
    const commitDetailsMap: Record<string, GitCommit> = allCommitDetails
      .filter(Boolean)
      .reduce((acc, commit) => ({ ...acc, [commit!.hash.slice(0, 9)]: commit }), {});

    return blameLines.map((blameLine) => {
      const { sha } = blameLine;
      const commitDetails = commitDetailsMap[sha];
      return {
        ...blameLine,
        date: commitDetails?.date,
        title: commitDetails?.title || 'Uncommitted changes',
        description: commitDetails?.description,
        author: commitDetails?.authorName || blameLine.author,
      };
    });
  }

  async getHistory(skip: number, limit: number, path?: FilePath): Promise<GitCommit[]> {
    return this.wasmGit.gitLog(['--', `--skip=${skip}`, `--max-count=${limit}`, ...(path ? [path] : [])]);
  }

  async getCommitDetails(shaOrPath: string, params?: string[]): Promise<GitCommit | null> {
    const commits = await this.wasmGit.gitLog(['--', `--max-count=1`, ...(params?.length ? params : []), shaOrPath]);
    return commits[0] || null;
  }

  async hasHistory(path: FilePath): Promise<boolean> {
    const commits = await this.getHistory(0, 1, path);
    return !!commits.length;
  }

  async getFilesWithoutHistory(paths: string[]): Promise<GitFileChange[]> {
    const pathsWithoutHistory = (
      await Promise.all(paths.map(async (path) => ((await this.hasHistory(path)) ? null : path)))
    ).filter((path): path is string => !!path);
    const status = await this.wasmGit.gitStatusShort();
    const branch = this.gitQuery.getActiveBranch().name;
    const changes = await Promise.all(pathsWithoutHistory.map((path) => this.getFileChange(branch, path)));
    return changes.map((change) => {
      const [__, ...statusRow] = status.find(([path]) => path === change.path) || [];
      return { ...change, status: statusRow as StatusRow };
    });
  }

  async getComparedHistory(
    compareWithBranch: GitBranch,
    compareDirection: GitCompareDirection,
    skip: number,
    limit: number,
  ): Promise<GitCommit[]> {
    const { name } = this.gitQuery.getActiveBranch();
    const compareWith = normalizeBranchName(compareWithBranch);
    const range = compareDirection === 'behind' ? `${name}..${compareWith}` : `${compareWith}..${name}`;
    return this.wasmGit.gitLog([range, '--', `--skip=${skip}`, `--max-count=${limit}`]);
  }

  async getComparedAheadBehindStatus(
    compareWithBranch: GitBranch,
    fromBranch?: GitBranch,
  ): Promise<GitAheadBehindStatus> {
    fromBranch = fromBranch ?? this.gitQuery.getActiveBranch();

    const result = await this.wasmGit.gitRevList([
      '--left-right',
      `${fromBranch.name}..${normalizeBranchName(compareWithBranch)}`,
    ]);
    const [ahead, behind] = result.split(' ');
    return {
      ahead: ahead || '',
      behind: behind || '',
    };
  }

  async renameBranch(newName: string) {
    const { name, published } = this.gitQuery.getActiveBranch();
    const defaultBranchName = this.gitQuery.getDefaultBranchName();
    if (name === defaultBranchName) {
      return;
    }

    // rename local branch
    await this.wasmGit.gitBranch({ command: '-m', options: [name, newName] });

    // rename remote branch if exists on remote
    if (published) {
      // delete "old" remote branch
      await this.wasmGit.gitPush({ refSpec: `:refs/heads/${name}`, authToken: await this.authTokenFn() });
      // push the new branch to remote
      await this.wasmGit.gitPush({ authToken: await this.authTokenFn() });
      // set the upstream for renamed branch
      await this.wasmGit.gitBranch({ command: '-u', options: [`origin/${newName}`] });
    }

    this.gitStore.update(name, {
      name: newName,
    });
    await this.setActiveBranch({
      branch: newName,
    });
  }

  async deleteBranch({ branchToDelete, localDelete }: { branchToDelete?: string; localDelete?: boolean } = {}) {
    const { name } = this.gitQuery.getActiveBranch();
    if (!branchToDelete) {
      branchToDelete = name;
    }
    const defaultBranchName = this.gitQuery.getDefaultBranchName();
    if (branchToDelete === defaultBranchName) {
      return;
    }

    if (branchToDelete === name) {
      // switch to default branch only if current branch is being removed
      await this.setActiveBranch({
        branch: defaultBranchName,
      });
    }

    // in case if something is committed to branch it can't be removed on a first run due to
    // `Error: Error branch delete [-1] - could not remove ...: still present: `
    await repeatOnFail(() => this.wasmGit.gitBranch({ command: '-d', options: [branchToDelete!] }));
    if (!localDelete) {
      await repeatOnFail(async () =>
        this.wasmGit.gitPush({ refSpec: `:refs/heads/${branchToDelete}`, authToken: await this.authTokenFn() }),
      );
    }

    this.gitStore.remove(branchToDelete);
  }

  async revertCommits(numberOfCommitsToReset = 1) {
    await this.wasmGit.gitReset([`HEAD~${numberOfCommitsToReset}`]);
    await this.updateUIState();
  }

  /**
   * Removes the space git repository. If no repository is passed, it will attempt to delete the active one.
   */
  async hardReset(repoDir?: string) {
    repoDir = repoDir || this.gitQuery.getRepo()?.repoDir;

    if (!repoDir) {
      return;
    }

    try {
      await this.wasmGit.rmDir({ path: `${repoDir}`, cwd: '/', force: true });
    } catch (error) {
      const errnoError = error as GitFSErrnoError;

      // BUSY error is thrown when trying to remove the path used to mount the IDFS file system
      // Ignore it
      if (errnoError.errno !== GitFSErrno.BUSY) {
        console.error(`[Git]: could not remove folder "${repoDir}"`);
      }
    }
  }

  async revert(commit: GitCommit, paths?: string[]) {
    console.debug(`[Git]: reverting changes`);
    this.gitStore.setStatus('revert');

    if (paths) {
      // git checkout c5f567~1 -- file1/to/restore file2/to/restore
      // https://stackoverflow.com/questions/215718/how-can-i-reset-or-revert-a-file-to-a-specific-revision
      await this.wasmGit.gitCheckout({ commitSha: `${commit.hash}~1`, paths });
    } else {
      await this.wasmGit.gitRevert({ commitSha: commit.hash, isMergeCommit: commit.isMergeCommit });
    }

    const conflicts = await this.checkNumberOfConflictedFiles();
    if (conflicts === 0) {
      await this.createMergeCommit(paths ? `Revert ${paths.join(', ')}` : undefined);
      // cleaning up reverting state
      try {
        await Promise.all(['.git/MERGE_MSG', '.git/REVERT_HEAD'].map((path) => this.wasmGit.deleteFile({ path })));
      } catch (e) {
        // might not exist
      }
    }

    await applyTransaction(async () => {
      await this.updateFileTree();
      await this.updateFileStatuses();
      this.gitStore.setStatus(null);
    });
    this.gitStore.emit<GitEventChangesReverted>(GitEventType.ChangesReverted, {
      branch: this.gitQuery.getActiveBranchName()!,
    });
  }

  async fastForwardBranches() {
    const refs = await this.wasmGit.gitForEachRef();
    const eligibleBranches = getBranchesDifferingFromUpstream(refs).filter(
      (ref) => ref?.name !== `refs/heads/${this.gitQuery.getActiveBranch()?.name}`,
    );

    if (eligibleBranches.length) {
      await this.wasmGit.gitFetch({
        fastForwardingBranches: eligibleBranches.map(({ name, upstream }) => `${name}:${upstream}`),
        authToken: await this.authTokenFn(),
      });
    }
  }

  async updateListOfBranches(prune = false) {
    const branches = await this.wasmGit.gitBranchList();
    const activeBranch = this.gitQuery.getActiveBranch();

    if (prune) {
      // TODO: implement pruning
      // The following approach is quite dangerous as it can try to delete branches when they being are created.
      //
      // const localOnlyBranches = branches.filter((branch) => !branch.published && branch.name !== activeBranch?.name);
      // await Promise.all([
      //   localOnlyBranches.map((branch) =>
      //     this.deleteBranch({
      //       branchToDelete: branch.name,
      //       localDelete: true,
      //     }),
      //   ),
      // ]);
      // branches = branches.filter((branch) => branch.published);
    }

    // Do not change this to `gitQuery.getDefaultBranchName` as it's still undefined at this point.
    const defaultBranchName = branches.find((branch) => branch.default)?.name;
    assertNonNullable(defaultBranchName, `Expected "default branch name" to be defined`);
    this.gitStore.setDefault(defaultBranchName);

    if (activeBranch) {
      // don't loose active branch tree
      const newActiveBranch = branches.find((branch) => branch.name === activeBranch.name);
      if (newActiveBranch) {
        newActiveBranch.tree = activeBranch.tree;
      }
    }

    this.gitStore.set(branches);
  }

  async search(paths: string[], query: string, matchCase: boolean, useRegExp: boolean) {
    return this.wasmGit.search({ paths, query, matchCase, useRegExp });
  }

  async gitCall(command: string, updateUIStateOptions?: GitEventForceUpdateUI['params']) {
    const call = await this.wasmGit.gitCall(command);
    await this.updateUIState(updateUIStateOptions);
    return call;
  }

  protected async webWorkerCall<
    Action extends WorkerAction,
    Fn extends (params: WorkerFnParams<Action>) => ReturnType<Fn>,
  >(action: Action) {
    return this.wasmGit.call(action);
  }

  async updateUIState(config?: GitEventForceUpdateUI['params'], opt?: { emitEvent?: boolean }): Promise<void> {
    // TODO: remove default values, are commands should explicitly specify what they want to update.
    config = config || { files: true, aheadBehind: true, commitSha: true };
    const { branches, files, aheadBehind, commitSha, activeBranch } = config;
    const { emitEvent } = opt || { emitEvent: false };

    if (branches) {
      await this.updateListOfBranches(true);
    }

    if (files) {
      await Promise.all([this.updateFileTree(), this.updateFileStatuses(), this.checkNumberOfConflictedFiles()]);
    }

    if (aheadBehind) {
      await this.updateAheadBehindStatus();
    }

    if (commitSha) {
      await Promise.all([this.updateCurrentLocalCommitSha(), this.updateLastPushedCommitSha()]);
    }

    if (activeBranch) {
      const activeBranchName = await this.getCurrentBranch();
      await this.gitStore.setActive(activeBranchName);

      this.gitStore.emit<GitEventBranchChanged>(GitEventType.BranchChanged, {
        to: activeBranchName,
      });
    }

    if (emitEvent) {
      this.gitStore.emit<GitEventForceUpdateUI>(GitEventType.ForceUpdateUI, config);
    }
  }

  private async updateAheadBehindStatus(): Promise<void> {
    const { ahead, behind } = await this.wasmGit.gitStatusAheadBehind();
    this.gitStore.updateUI({ ahead, behind });
    console.debug(`[Git]: ahead by ${ahead}, behind by ${behind}`);
  }

  private async updateLastPushedCommitSha(): Promise<void> {
    const activeBranchName = this.gitQuery.getActiveBranchName();
    assertNonNullable(activeBranchName);
    const sha1 = await this.wasmGit.gitLsRemote({ branch: activeBranchName, authToken: await this.authTokenFn() });
    this.gitStore.setCurrentOriginCommitSha(sha1);
  }

  private async updateCurrentLocalCommitSha(): Promise<void> {
    const sha1 = await this.wasmGit.gitRevParse({ rev: 'HEAD' });
    this.gitStore.setCurrentLocalCommitSha(sha1);
  }

  private async updateFileStatuses(): Promise<void> {
    const fileStatusRows = await this.wasmGit.gitStatusShort();
    console.debug(`[Git]: git status --short`, fileStatusRows);
    this.gitStore.setFileStatuses(fileStatusRows);
  }

  private async checkNumberOfConflictedFiles(): Promise<number> {
    console.debug(`[Git]: checking for conflicts`);

    const conflicts = await this.getPathsOfConflictedFiles();

    console.debug(`[Git]: ${conflicts?.length || 0} conflicts found`);
    const numberOfConflictedFiles = conflicts?.length;
    this.gitStore.updateUI({ numberOfConflictedFiles });
    let prevStatus = this.gitStore.getValue()?.ui?.status;
    if (prevStatus?.status === 'conflict') {
      prevStatus = null;
    }
    if (numberOfConflictedFiles) {
      let mergeHead = '';
      try {
        mergeHead = await this.wasmGit.readFile({ path: '.git/MERGE_HEAD' });
      } catch (e) {
        // might not exist
      }
      let conflictType = '';
      if (mergeHead) {
        conflictType = 'merge';
      }

      this.gitStore.setStatus({ status: 'conflict', params: conflictType });
    } else {
      this.gitStore.setStatus(prevStatus);
    }

    return numberOfConflictedFiles;
  }

  private async getPathsOfConflictedFiles(): Promise<Array<[FilePath, StatusRow]>> {
    const FILE = 0;
    const files = await this.wasmGit.gitStatusShort();
    const conflictedStatuses = [
      GitStatusToStatusRow.UU,
      GitStatusToStatusRow.AA,
      GitStatusToStatusRow.DU,
      GitStatusToStatusRow.UD,
      GitStatusToStatusRow.DD,
      GitStatusToStatusRow.AU,
      GitStatusToStatusRow.UA,
    ];

    const conflictedFiles = files
      .map((row) => [row[FILE], row.slice(1).join('')])
      .filter((row) => conflictedStatuses.map((status) => status.join('')).includes(row[1] || ''))
      .map(
        (row) => [row[FILE], conflictedStatuses.find((status) => status.join('') === row[1])!] as [FilePath, StatusRow],
      );
    console.debug(`[Git]: conflicted files`, conflictedFiles);

    return conflictedFiles;
  }

  private async updateFileTree(path = './') {
    console.debug(`[Git]: reading file tree at "${path}"`);
    const { tree, numberOfFiles } = await this.wasmGit.readDir({ path, countChildren: true });

    console.debug(`[Git]: read file tree`, tree);
    this.gitStore.updateActive({ tree, numberOfFiles });
  }

  private async getFileChange(branch: string, path: FilePath): Promise<Omit<GitFileChange, 'status'>> {
    let original: string | undefined;

    let modified = '';

    try {
      modified = await this.wasmGit.readFile({ path });
    } catch (e) {
      // might not exist if file was deleted
    }

    try {
      original = await this.wasmGit.gitShow({ branch, path });
    } catch (e) {
      // original might not exist on branch if file is new
    }

    return {
      path,
      modified,
      original,
      fileName: path.split('/').reverse()[0] || '',
    };
  }
}

function normalizeBranchName(branch: GitBranch) {
  return `${!branch.local ? 'origin/' : ''}${branch.name}`;
}

/**
 * Gets all branches that differ from their upstream (i.e. they're ahead,
 * behind or both), excluding the current branch.
 * They can potentially be fast forwarded.
 */
function getBranchesDifferingFromUpstream(refs: GitRef[]): GitRef[] {
  const localRefs = refs.filter((ref) => ref.name.startsWith('refs/heads'));
  const remoteBranchShas: Record<string, string> = refs
    .filter((ref) => !ref.name.startsWith('refs/heads'))
    .reduce((acc, cur) => ({ ...acc, [cur.name]: cur.sha }), {});

  const eligibleBranches = [];

  // Compare the SHA of every local branch with the SHA of its upstream and
  // collect the names of local branches that differ from their upstream.
  for (const ref of localRefs) {
    const remoteSha = remoteBranchShas[ref.upstream!];

    if (remoteSha && remoteSha !== ref.sha) {
      eligibleBranches.push({
        name: ref.name,
        sha: ref.sha,
        upstream: ref.upstream,
      });
    }
  }

  return eligibleBranches;
}

interface File {
  path: string;
  propertyPath?: string[];
  content?: object | string;
}

async function repeatOnFail(fn: () => Promise<string | void>) {
  try {
    await fn();
  } catch (err) {
    console.debug(err instanceof Error ? err.message : `${err}`);
    await fn();
  }
}

function parsePathContent(path: string, content: string): unknown {
  if (path.endsWith('.json')) {
    return safeJsonParse(content);
  }

  if (['.yml', '.yaml'].some((ext) => path.endsWith(ext))) {
    return safeYAMLParse(content);
  }

  return content;
}
